import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Field, Badge, Chip, Center, Spinner, KV, PageHeader } from "@/components/ui";
import { Topbar } from "@/components/Layout";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ManageStaff() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null); const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api.get("/office/staff"); setStaff(d.staff || []); setRoles(d.roles || []); }
    catch (e) { if (e instanceof ApiError && e.status === 403) nav("/office"); } finally { setLoading(false); }
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const addStaff = async () => {
    setErr(null); setCreated(null);
    if (!name.trim() || !email.trim() || !role) { setErr("Enter a name, email, and role."); return; }
    setBusy(true);
    try { const res = await api.post("/office/staff", { full_name: name.trim(), email: email.trim(), role }); setCreated(res); setName(""); setEmail(""); setRole(""); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : "Could not create staff."); } finally { setBusy(false); }
  };
  const toggleStatus = async (s) => { try { await api.post(`/office/staff/${s.id}/status`, { status: s.status === "active" ? "disabled" : "active" }); await load(); } catch {} };

  if (loading) return <div className="screen"><Topbar title="Manage staff" /><Center><Spinner /></Center></div>;

  return (
    <div className="screen" data-testid="manage-staff-screen">
      <Topbar title="Manage staff" subtitle="Invite & manage back-office team" />
      <div className="container-narrow">
        <PageHeader title="Manage staff" onBack={() => nav("/office")} />
        <Card>
          <div className="tag">INVITE A TEAMMATE</div>
          <Field testID="staff-name" label="Full name" value={name} onChange={setName} placeholder="Jane Doe" />
          <Field testID="staff-email" label="Email" value={email} onChange={setEmail} placeholder="jane@youragency.com" />
          <div className="section-label">ROLE</div>
          <div className="chip-wrap">{roles.map((r) => <Chip key={r} testID={`role-${r}`} label={r.replace(/_/g, " ")} selected={role === r} onClick={() => setRole(r)} />)}</div>
          {err ? <p className="err-text" data-testid="staff-error">{err}</p> : null}
          <div style={{ height: 12 }} />
          <Button testID="staff-create" label="Create account & send invite" onClick={addStaff} loading={busy} />
          {created ? <div className="avail-box" data-testid="staff-created">
            <Badge label={created.invite_sent ? "Invite email sent" : "Account created — share the temp password"} tone={created.invite_sent ? "success" : "warning"} />
            <KV k="email" v={created.email} mono /><KV k="temp password" v={created.temp_password} mono />
            <p className="note">They set their own password + get an emailed code on first sign-in.</p>
          </div> : null}
        </Card>
        <div className="section-label">TEAM ({staff.length})</div>
        {staff.map((s) => (
          <Card key={s.id} testID={`staff-${s.id}`}>
            <div className="between">
              <div><b>{s.full_name}</b><div className="row-sub">{s.email}</div>
                <div className="row-meta"><Badge label={s.role.replace(/_/g, " ")} tone="neutral" /><Badge label={s.status} tone={s.status === "active" ? "success" : "warning"} />{s.must_reset_password ? <Badge label="pending first login" tone="neutral" /> : null}</div></div>
              {s.id !== user?.id ? <Button small testID={`toggle-${s.id}`} variant="ghost" label={s.status === "active" ? "Disable" : "Enable"} onClick={() => toggleStatus(s)} /> : <span className="row-sub">you</span>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
